import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Plus, Pencil, Trash2, Lock, ArrowRight, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { GlassCard, API } from './GlassCard';

const NEXT_STEP_ROUTE = {
  profile_picture: '/dashboard/vendor/profile',
  ethnicity:       '/dashboard/vendor/profile',
  bio:             '/dashboard/vendor/profile',
  business_name:   '/dashboard/vendor/profile',
  description:     '/dashboard/vendor/profile',
  location:        '/dashboard/vendor/profile',
  stripe_connected: '/vendor/setup-payouts',
};
const NEXT_STEP_LABEL = {
  profile_picture: 'Upload a profile picture',
  ethnicity:       'Add your tribal / racial affiliation',
  bio:             'Write a short bio',
  business_name:   'Enter your business name',
  description:     'Write a short business description',
  location:        'Add your location',
  stripe_connected: 'Set up your payouts',
};

export default function Services() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    fetchServices();
    fetchReadiness();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = async () => {
    try {
      const response = await fetch(`${API}/services/my-services`, { headers: getAuthHeaders() });
      if (response.ok) setServices(await response.json());
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchReadiness = async () => {
    try {
      const r = await fetch(`${API}/completion/vendor`, { headers: getAuthHeaders() });
      if (r.ok) setReadiness(await r.json());
    } catch {}
  };

  const deleteService = async (serviceId) => {
    try {
      const response = await fetch(`${API}/services/${serviceId}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setServices(services.filter(s => s.service_id !== serviceId));
        toast.success('Service deleted');
      }
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const canSell = readiness?.ready_to_sell !== false;
  const nextStep = readiness?.next_required_step;
  const handleAddClick = (e) => {
    if (!canSell && readiness) {
      e.preventDefault();
      toast.error(`Finish your profile first — ${NEXT_STEP_LABEL[nextStep] || 'complete the required steps'}.`);
      if (NEXT_STEP_ROUTE[nextStep]) {
        navigate(NEXT_STEP_ROUTE[nextStep]);
      }
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-syne" data-testid="services-title">My Services</h1>
          <p className="text-sm text-[#9CA3AF] mt-1">Manage your service offerings</p>
        </div>
        {canSell ? (
          <Link to="/dashboard/vendor/services/new">
            <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] hover:shadow-[0_0_30px_rgba(139,92,246,0.5)] transition-all text-sm" data-testid="add-service-btn">
              <Plus size={16} /> Add Service
            </button>
          </Link>
        ) : (
          <button onClick={handleAddClick} className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white/60 bg-white/5 border border-white/10 cursor-not-allowed text-sm" data-testid="add-service-btn-locked" title="Finish profile setup to list services">
            <Lock size={14} /> Add Service
          </button>
        )}
      </div>

      {/* Profile-incomplete gate banner */}
      {readiness && !canSell && (
        <GlassCard className="p-5 border border-amber-400/30 bg-amber-500/5" data-testid="sell-gate-banner">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center flex-shrink-0">
              <Lock size={18} className="text-amber-300" />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider font-bold text-amber-200">Finish your profile to start selling</p>
              <h3 className="text-white text-lg font-bold mt-1" style={{ fontFamily: 'Syne, system-ui' }}>You're {readiness.percentage}% set up</h3>
              <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden my-3">
                <div className="h-full bg-gradient-to-r from-amber-400 to-amber-300 rounded-full transition-all" style={{ width: `${readiness.percentage}%` }} />
              </div>
              <p className="text-sm text-white/70">
                Before listing services, complete the critical steps so customers can find you, trust you, and pay you:
              </p>
              <ul className="mt-3 space-y-1.5 text-sm">
                {(readiness.critical_steps || []).map(id => {
                  const step = (readiness.steps || []).find(s => s.id === id) || {};
                  return (
                    <li key={id} className="flex items-center gap-2">
                      {step.done ? <CheckCircle size={14} className="text-emerald-400" /> : <div className="w-3.5 h-3.5 rounded-full border-2 border-amber-300" />}
                      <span className={step.done ? 'line-through text-white/40' : 'text-white/85'}>
                        {NEXT_STEP_LABEL[id] || step.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {nextStep && NEXT_STEP_ROUTE[nextStep] && (
                <button
                  onClick={() => navigate(NEXT_STEP_ROUTE[nextStep])}
                  className="mt-4 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-[#0E0E23] font-bold text-sm uppercase tracking-wider"
                  data-testid="sell-gate-continue-btn"
                >
                  Continue setup <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        </GlassCard>
      )}

      {services.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service) => (
            <GlassCard key={service.service_id} className="p-6" data-testid={`service-${service.service_id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold">{service.title}</h3>
                  <p className="text-[#9CA3AF] text-sm line-clamp-2 mt-1">{service.description}</p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => navigate(`/dashboard/vendor/services/${service.service_id}/edit`)}
                    className="p-2 rounded-lg text-[#9CA3AF] hover:text-[#A78BFA] hover:bg-white/5 transition-colors"
                    title="Edit service"
                    data-testid={`edit-service-${service.service_id}`}
                  >
                    <Pencil size={16} />
                  </button>
                  <button className="p-2 rounded-lg text-[#9CA3AF] hover:text-[#FF6B6B] hover:bg-white/5 transition-colors" onClick={() => deleteService(service.service_id)} data-testid={`delete-service-${service.service_id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {service.packages?.map((pkg) => (
                  <div key={pkg.name} className="flex items-center justify-between text-sm p-2.5 rounded-xl bg-white/5">
                    <span className="text-[#9CA3AF] capitalize">{pkg.name}</span>
                    <span className="text-[#A78BFA] font-semibold">${pkg.price}</span>
                  </div>
                ))}
              </div>
            </GlassCard>
          ))}
        </div>
      ) : (
        canSell && (
          <GlassCard className="text-center py-12 px-6">
            <Package size={40} className="mx-auto mb-4 text-[#9CA3AF] opacity-40" />
            <p className="text-[#9CA3AF] mb-4">No services yet</p>
            <Link to="/dashboard/vendor/services/new">
              <button className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all text-sm">
                Create Your First Service
              </button>
            </Link>
          </GlassCard>
        )
      )}
    </div>
  );
}
